import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';

import { createAppAsyncThunk } from '../../utils/reduxUtil';
import { REPORT_API_URL } from '../../services/config';
import { api } from '../../services/axios';

export interface ReportItem {
  id: string;
  title: string;
  price: number;
  count: number;
  totalPrice: number;
}

interface VendorReportsStateInterface {
  loading: boolean;
  summary: {
    count: number;
    total: number;
    tip: number;
  };
  detail: Array<ReportItem>;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

const initialState: VendorReportsStateInterface = {
  loading: false,
  summary: {
    count: 0,
    total: 0,
    tip: 0,
  },
  detail: [],
  totalCount: 0,
  pageNumber: 0,
  pageSize: 0,
};

interface GetVendorReportResponse {
  summary: {
    endDate: Date;
    startDate: Date;
    endTime: string;
    startTime: string;
    orderCount: number;
    totalSale: number;
    totalTip: number;
  };
}

interface GetVendorReportRequest {
  vendorId: string;
  from: string;
  to: string;
  fromHour?: string;
  toHour?: string;
  pageNumber?: number;
  pageSize?: number;
}

interface GetVendorReportItemsResponse {
  data: Array<ReportItem>;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

const getVendorHourlyReportsItems = createAppAsyncThunk<GetVendorReportItemsResponse, GetVendorReportRequest>(
  'reports/getHourlyVendorReportItems',
  async ({ vendorId, from, to, fromHour, toHour, pageNumber, pageSize }) => {
    const response = await api.get(`${REPORT_API_URL}SyncOrders/ItemsReport/${vendorId}`, {
      params: { from, to, fromHour, toHour, pageNumber, pageSize },
    });
    return response.data;
  }
);

const getVendorHourlyReports = createAppAsyncThunk<GetVendorReportResponse, GetVendorReportRequest>(
  'reports/getHourlyVendorReports',
  async ({ vendorId, from, to, fromHour, toHour, pageNumber, pageSize }, { dispatch }) => {
    const response = await api.get(`${REPORT_API_URL}SyncOrders/OrderReport/${vendorId}`, {
      params: { from, to, fromHour, toHour },
    });
    dispatch(
      getVendorHourlyReportsItems({
        from,
        to,
        vendorId,
        fromHour,
        pageNumber,
        pageSize,
        toHour,
      })
    );
    return response.data;
  }
);

const reportHourlySlice = createSlice({
  name: 'reportHourly',
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<VendorReportsStateInterface>) => {
    builder.addCase(getVendorHourlyReports.pending, (state) => ({
      ...state,
      loading: true,
      summary: {
        count: 0,
        tip: 0,
        total: 0,
      },
      detail: [],
      pageNumber: 0,
      pageSize: 0,
      totalCount: 0,
    }));
    builder.addCase(getVendorHourlyReports.fulfilled, (state, action) => {
      const { summary } = action.payload;
      return {
        ...state,
        loading: false,
        summary: {
          ...state.summary,
          count: summary?.orderCount,
          tip: summary.totalTip,
          total: summary.totalSale,
        },
      };
    });
    builder.addCase(getVendorHourlyReports.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(getVendorHourlyReportsItems.pending, (state) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(getVendorHourlyReportsItems.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      detail: action.payload.data,
      pageNumber: action.payload.pageNumber,
      pageSize: action.payload.pageSize,
      totalCount: action.payload.totalCount,
    }));
    builder.addCase(getVendorHourlyReportsItems.rejected, (state) => ({
      ...state,
      loading: false,
    }));
  },
});

export default reportHourlySlice;
export { getVendorHourlyReports, getVendorHourlyReportsItems };
